import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../auth/AuthContext';
import apiService from '../../services/api';
import { Link } from 'react-router-dom';
import { areAllPhasesComplete } from '../Questionnaires/Questionnaires';
import {
  Container,
  Stack,
  Grid,
  Card,
  Title,
  Text,
  Group,
  Badge,
  Button,
  Skeleton,
  Alert,
  RingProgress,
  ThemeIcon,
  SimpleGrid,
  Progress,
  Paper,
  Divider,
} from '@mantine/core';
import {
  IconHeartbeat,
  IconHeart,
  IconFlask,
  IconScan,
  IconClipboardCheck,
  IconActivity,
  IconMoon,
  IconBolt,
  IconChevronRight,
  IconDroplet,
  IconFlame,
  IconPill,
  IconStethoscope,
  IconBone,
  IconScale,
  IconShieldCheck,
  IconAlertCircle,
  IconCircleCheck,
  IconDeviceWatch,
} from '@tabler/icons-react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import './Dashboard.css';

// ============================================================
// HELPERS
// ============================================================

/* Soft, warm palette for progress and badges */
const STATUS_COLORS = {
  optimal: { color: 'teal', shade: '#7dd3b0' },
  borderline: { color: 'yellow', shade: '#fde68a' },
  concerning: { color: 'red', shade: '#fecaca' },
  unknown: { color: 'gray', shade: '#d6d3c4' },
};

function getStatus(score) {
  if (score === null || score === undefined) return 'unknown';
  if (score >= 80) return 'optimal';
  if (score >= 50) return 'borderline';
  return 'concerning';
}

const CATEGORY_LABELS = {
  // Lab categories
  hormones: { label: 'Hormones', icon: IconStethoscope, color: 'violet' },
  glucose: { label: 'Glucose', icon: IconDroplet, color: 'orange' },
  inflammation: { label: 'Inflammation', icon: IconFlame, color: 'red' },
  lipids: { label: 'Lipids', icon: IconHeartbeat, color: 'pink' },
  liver_kidney: { label: 'Liver & Kidney', icon: IconPill, color: 'grape' },
  cbc: { label: 'Blood Counts', icon: IconDroplet, color: 'red' },
  nutrients: { label: 'Nutrients', icon: IconShieldCheck, color: 'green' },
  genetic_metals: { label: 'Genetic & Metals', icon: IconAlertCircle, color: 'gray' },
  // DEXA categories
  body_composition: { label: 'Body Composition', icon: IconScale, color: 'cyan' },
  bone_health: { label: 'Bone Health', icon: IconBone, color: 'indigo' },
  visceral_fat: { label: 'Visceral Fat', icon: IconFlame, color: 'orange' },
  lean_mass: { label: 'Lean Mass', icon: IconActivity, color: 'teal' },
  derived: { label: 'Derived Indices', icon: IconStethoscope, color: 'blue' },
};

function formatDuration(seconds) {
  if (!seconds) return '--';
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
}

// ============================================================
// COMPONENT
// ============================================================

function HealthDashboard() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [labResults, setLabResults] = useState([]);
  const [dexaResults, setDexaResults] = useState([]);
  const [wearableConnected, setWearableConnected] = useState(false);
  const [wearableData, setWearableData] = useState({ restingHR: null, restingHRDate: null, sleep: null, sleepDate: null, recovery: null, recoveryDate: null });
  const [questionnaireStatus, setQuestionnaireStatus] = useState(null); // null = loading, 'completed' | 'not_started'

  const fetchData = useCallback(async () => {
    if (!user) return;
    setLoading(true);

    const [labRes, dexaRes, wearConn, draftRes, typesRes] = await Promise.allSettled([
      apiService.labResults.getResults(),
      apiService.dexaScans.getResults(),
      apiService.wearables.getConnections(),
      apiService.questionnaires.getDraft('intake'),
      apiService.questionnaires.getTypes(),
    ]);

    // Lab results
    if (labRes.status === 'fulfilled') {
      setLabResults(labRes.value.data || []);
    }

    // DEXA results
    if (dexaRes.status === 'fulfilled') {
      setDexaResults(dexaRes.value.data || []);
    }

    // Questionnaire: completed only when all phases are complete (draft + schema)
    const draft = draftRes.status === 'fulfilled' ? draftRes.value.data?.draft : null;
    const types = typesRes.status === 'fulfilled' ? typesRes.value.data?.types : null;
    const phases = types?.intake?.phases ?? [];
    const responses = draft?.responses;
    const hasDraftWithData = responses && Object.keys(responses).length > 0;
    const allPhasesComplete = hasDraftWithData && areAllPhasesComplete(responses, phases);
    setQuestionnaireStatus(allPhasesComplete ? 'completed' : hasDraftWithData ? 'in_progress' : 'not_started');

    // Wearables
    const connections = wearConn.status === 'fulfilled' ? (wearConn.value.data?.connections || []) : [];
    const hasConnection = connections.length > 0;
    setWearableConnected(hasConnection);

    if (hasConnection) {
      const endDt = new Date();
      const startDt = new Date();
      startDt.setDate(startDt.getDate() - 12000);
      const startDate = startDt.toISOString().split('T')[0];
      const endDate = endDt.toISOString().split('T')[0];
      const startTime = `${startDate}T00:00:00Z`;
      const endTime = `${endDate}T23:59:59Z`;

      const [sleepRes, tsRes] = await Promise.allSettled([
        apiService.wearables.getSleepSessions(startDate, endDate, 50),
        apiService.wearables.getTimeseries(startTime, endTime, ['recovery_score', 'resting_heart_rate'], 100, 'raw'),
      ]);

      const sleepSessions = sleepRes.status === 'fulfilled' ? (sleepRes.value.data?.data || sleepRes.value.data || []) : [];
      const tsData = tsRes.status === 'fulfilled' ? (tsRes.value.data?.data || tsRes.value.data || []) : [];

      const sortedSleep = [...sleepSessions].sort((a, b) => new Date(b.start_time || 0) - new Date(a.start_time || 0));
      const latestSleep = sortedSleep[0];

      const recoveryPoints = tsData.filter(d => d.type === 'recovery_score');
      const hrPoints = tsData.filter(d => d.type === 'resting_heart_rate');
      const latestRec = [...recoveryPoints].sort((a, b) => new Date(b.timestamp || 0) - new Date(a.timestamp || 0))[0];
      const latestHR = [...hrPoints].sort((a, b) => new Date(b.timestamp || 0) - new Date(a.timestamp || 0))[0];

      setWearableData({
        restingHR: latestHR?.value || null,
        restingHRDate: latestHR?.timestamp || null,
        sleep: latestSleep?.duration_seconds || latestSleep?.total_sleep_time_seconds || null,
        sleepDate: latestSleep?.start_time || null,
        recovery: latestRec?.value || null,
        recoveryDate: latestRec?.timestamp || null,
      });
    }

    setLoading(false);
  }, [user]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // ---- Derived data ----

  const latestLab = labResults.length > 0 ? labResults[labResults.length - 1] : null;
  const latestDexa = dexaResults.length > 0 ? dexaResults[dexaResults.length - 1] : null;

  const labScore = latestLab?.scores?.overall ?? null;
  const dexaScore = latestDexa?.scores?.overall ?? null;
  const scoreValues = [labScore, dexaScore].filter((s) => s !== null);
  const overallScore = scoreValues.length > 0
    ? Math.round(scoreValues.reduce((a, b) => a + b) / scoreValues.length)
    : null;
  const overallStatus = getStatus(overallScore);

  // Category cards: combine lab + DEXA categories
  const categoryCards = [];
  if (latestLab?.scores?.categories) {
    for (const [key, data] of Object.entries(latestLab.scores.categories)) {
      if (data.score !== null) {
        categoryCards.push({ key, ...data, source: 'lab' });
      }
    }
  }
  if (latestDexa?.scores?.categories) {
    for (const [key, data] of Object.entries(latestDexa.scores.categories)) {
      if (data.score !== null) {
        categoryCards.push({ key, ...data, source: 'dexa' });
      }
    }
  }

  // Trend data
  const labTrend = labResults
    .filter((r) => r.scores?.overall != null && r.patient_info?.test_date)
    .map((r) => ({
      date: r.patient_info.test_date,
      score: r.scores.overall,
    }))
    .sort((a, b) => new Date(a.date) - new Date(b.date));

  const dexaTrend = dexaResults
    .filter((r) => r.scores?.overall != null)
    .map((r) => ({
      date: r.scan_date || r.patient_info?.scan_date || r.created_at,
      score: r.scores.overall,
    }))
    .sort((a, b) => new Date(a.date) - new Date(b.date));

  const hasAnyData = labResults.length > 0 || dexaResults.length > 0 || wearableConnected || questionnaireStatus === 'completed' || questionnaireStatus === 'in_progress';

  // ---- Loading state ----

  if (loading) {
    return (
      <Container size="xl" py={{ base: 'lg', sm: 'xl' }} maw={1200}>
        <Stack gap="lg">
          <Skeleton height={36} width={200} />
          <Grid>
            <Grid.Col span={{ base: 12, md: 4 }}>
              <Skeleton height={200} radius="md" />
            </Grid.Col>
            <Grid.Col span={{ base: 12, md: 8 }}>
              <Skeleton height={200} radius="md" />
            </Grid.Col>
          </Grid>
          <Grid>
            {[1, 2, 3, 4].map((i) => (
              <Grid.Col key={i} span={{ base: 6, sm: 3 }}>
                <Skeleton height={120} radius="md" />
              </Grid.Col>
            ))}
          </Grid>
        </Stack>
      </Container>
    );
  }

  // ---- Empty state ----

  if (!hasAnyData) {
    return (
      <div className="dashboard-page">
        <Container size="xl" py={{ base: 'lg', sm: 'xl' }} maw={1200}>
          <Stack gap="lg">
            <Title order={1} size="h2" className="dashboard-main-title">Your Dashboard</Title>

            <Card withBorder radius="lg" p="xl" className="dashboard-card" style={{ textAlign: 'center' }}>
            <Stack gap="md" align="center">
              <ThemeIcon size={64} radius="xl" variant="light" color="pink">
                <IconHeartbeat size={32} />
              </ThemeIcon>
              <Title order={3}>Welcome to Your Health Dashboard</Title>
              <Text c="dimmed" maw={500}>
                Start building your health profile by uploading lab results,
                connecting a wearable device, or completing your health questionnaire.
              </Text>
            </Stack>

            <SimpleGrid cols={{ base: 1, sm: 3 }} mt="xl" spacing="md">
              <Card withBorder radius="md" p="md" component={Link} to="/lab-results" className="dashboard-card dashboard-link-card"
                style={{ textDecoration: 'none', cursor: 'pointer', transition: 'transform 0.15s' }}>
                <Stack gap="xs" align="center">
                  <ThemeIcon size={40} radius="md" variant="light" color="pink">
                    <IconFlask size={20} />
                  </ThemeIcon>
                  <Text fw={600} size="sm">Upload Lab Results</Text>
                  <Text c="dimmed" size="xs">Analyze your blood work</Text>
                </Stack>
              </Card>

              <Card withBorder radius="md" p="md" component={Link} to="/wearables/devices" className="dashboard-card dashboard-link-card"
                style={{ textDecoration: 'none', cursor: 'pointer' }}>
                <Stack gap="xs" align="center">
                  <ThemeIcon size={40} radius="md" variant="light" color="violet">
                    <IconDeviceWatch size={20} />
                  </ThemeIcon>
                  <Text fw={600} size="sm">Connect Device</Text>
                  <Text c="dimmed" size="xs">Sync your wearable data</Text>
                </Stack>
              </Card>

              <Card withBorder radius="md" p="md" component={Link} to="/questionnaires" className="dashboard-card dashboard-link-card"
                style={{ textDecoration: 'none', cursor: 'pointer' }}>
                <Stack gap="xs" align="center">
                  <ThemeIcon size={40} radius="md" variant="light" color="teal">
                    <IconClipboardCheck size={20} />
                  </ThemeIcon>
                  <Text fw={600} size="sm">Health Questionnaire</Text>
                  <Text c="dimmed" size="xs">Complete your intake form</Text>
                </Stack>
              </Card>
            </SimpleGrid>
            </Card>
          </Stack>
        </Container>
      </div>
    );
  }

  // ---- Main dashboard ----

  return (
    <div className="dashboard-page">
      <Container size="xl" py={{ base: 'lg', sm: 'xl' }} maw={1200}>
        <Stack gap="lg">
          <Title order={1} size="h2" className="dashboard-main-title">Your Dashboard</Title>

          {/* ---- Section 1: Overall Score + Summary ---- */}
          <Grid gutter="lg">
            <Grid.Col span={{ base: 12, md: 4 }}>
              <Card withBorder radius="lg" p="lg" h="100%" className="dashboard-card">
              <Stack align="center" justify="center" h="100%" gap="sm">
                {overallScore !== null ? (
                  <>
                    <RingProgress
                      size={160}
                      thickness={14}
                      roundCaps
                      sections={[{
                        value: overallScore,
                        color: STATUS_COLORS[overallStatus].shade,
                      }]}
                      label={
                        <Text ta="center" fw={700} size="2rem" lh={1}>
                          {overallScore}
                        </Text>
                      }
                    />
                    <Badge
                      size="lg"
                      variant="light"
                      color={STATUS_COLORS[overallStatus].color}
                      tt="capitalize"
                    >
                      {overallStatus}
                    </Badge>
                    <Text size="sm" c="dimmed" ta="center">Overall Health Score</Text>
                  </>
                ) : (
                  <>
                    <ThemeIcon size={64} radius="xl" variant="light" color="gray">
                      <IconHeartbeat size={32} />
                    </ThemeIcon>
                    <Text c="dimmed" ta="center" size="sm">
                      Upload lab results or a DEXA scan to see your score
                    </Text>
                  </>
                )}
              </Stack>
            </Card>
          </Grid.Col>

          <Grid.Col span={{ base: 12, md: 8 }}>
            <Card withBorder radius="lg" p="lg" h="100%" className="dashboard-card dashboard-setup-card">
              <Title order={4} mb="sm" className="dashboard-section-title">Data Sources</Title>
              <div className="dashboard-setup-grid">
                {[
                  {
                    label: 'Questionnaire',
                    done: questionnaireStatus === 'completed',
                    inProgress: questionnaireStatus === 'in_progress',
                    doneLabel: 'Done',
                    to: '/questionnaires',
                    icon: IconClipboardCheck,
                    color: 'teal',
                  },
                  {
                    label: 'Lab Results',
                    done: labResults.length > 0,
                    inProgress: false,
                    doneLabel: 'Add New',
                    to: '/lab-results',
                    icon: IconFlask,
                    color: 'pink',
                  },
                  {
                    label: 'DEXA Scan',
                    done: dexaResults.length > 0,
                    inProgress: false,
                    doneLabel: 'Add New',
                    to: '/dexa-scans',
                    icon: IconScan,
                    color: 'indigo',
                  },
                  {
                    label: 'Wearables',
                    done: wearableConnected,
                    inProgress: false,
                    doneLabel: 'Connected',
                    to: '/wearables/devices',
                    icon: IconDeviceWatch,
                    color: 'violet',
                  },
                ].map((item) => (
                  <Paper
                    key={item.label}
                    withBorder
                    radius="md"
                    p="sm"
                    component={Link}
                    to={item.to}
                    className="dashboard-setup-item dashboard-link-card"
                    style={{ textDecoration: 'none', cursor: 'pointer', transition: 'background 0.15s' }}
                  >
                    <Stack align="center" gap={8} h="100%" justify="space-between">
                      <ThemeIcon
                        size={36}
                        radius="md"
                        variant="light"
                        color={item.done ? item.color : (item.inProgress ? item.color : 'gray')}
                      >
                        <item.icon size={18} />
                      </ThemeIcon>
                      <Text size="xs" fw={600} ta="center" className="dashboard-setup-label" c="#000">{item.label}</Text>
                      {item.done ? (
                        <Badge size="sm" variant="light" color="teal" leftSection={<IconCircleCheck size={12} />}>
                          {item.doneLabel}
                        </Badge>
                      ) : item.inProgress ? (
                        <Text size="xs" c="teal" fw={600}>In progress</Text>
                      ) : (
                        <Text size="xs" c="pink" fw={600}>Add</Text>
                      )}
                    </Stack>
                  </Paper>
                ))}
              </div>
            </Card>
          </Grid.Col>
        </Grid>

        {/* ---- Section 2: Category Scores ---- */}
        {categoryCards.length > 0 && (
          <>
            <Divider />
            <div>
              <Title order={3} size="h4" mb="sm" className="dashboard-section-title">Health Category Scores</Title>
              <SimpleGrid cols={{ base: 2, sm: 3, md: 4 }} spacing="sm">
                {categoryCards.map(({ key, score, status }) => {
                  const meta = CATEGORY_LABELS[key] || { label: key, icon: IconHeartbeat, color: 'gray' };
                  const Icon = meta.icon;
                  return (
                    <Card
                      key={key}
                      withBorder
                      radius="md"
                      p="md"
                      className="dashboard-card dashboard-link-card"
                      component={Link}
                      to={key === 'body_composition' || key === 'bone_health' || key === 'visceral_fat' || key === 'lean_mass' || key === 'derived'
                        ? '/dexa-scans'
                        : latestLab ? `/lab-results?resultId=${latestLab.id}` : '/lab-results'}
                      style={{ textDecoration: 'none', cursor: 'pointer' }}
                    >
                      <Group justify="space-between" mb="xs">
                        <ThemeIcon size={28} radius="sm" variant="light" color={meta.color}>
                          <Icon size={16} />
                        </ThemeIcon>
                        <Badge
                          size="lg"
                          variant="light"
                          color={STATUS_COLORS[status || 'unknown'].color}
                        >
                          {score ?? '--'}
                        </Badge>
                      </Group>
                      <Text size="sm" fw={600} mt={4}>{meta.label}</Text>
                      <Progress
                        value={score || 0}
                        color={STATUS_COLORS[status || 'unknown'].shade}
                        size="sm"
                        radius="xl"
                        mt="xs"
                      />
                    </Card>
                  );
                })}
              </SimpleGrid>
            </div>
          </>
        )}

        {/* ---- Section 3: Trend Charts ---- */}
        {(labTrend.length >= 2 || dexaTrend.length >= 2) && (
          <>
            <Divider />
            <div>
              <Title order={3} size="h4" mb="sm" className="dashboard-section-title">Score Trends</Title>
              <Grid gutter="md">
                {labTrend.length >= 2 && (
                  <Grid.Col span={{ base: 12, md: dexaTrend.length >= 2 ? 6 : 12 }}>
                    <Card withBorder radius="md" p="md" className="dashboard-card">
                      <Text size="sm" fw={600} mb="sm">Lab Results Over Time</Text>
                      <ResponsiveContainer width="100%" height={220}>
                        <LineChart data={labTrend}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#f1f3f5" />
                          <XAxis
                            dataKey="date"
                            tick={{ fontSize: 11 }}
                            tickFormatter={(d) => {
                              const date = new Date(d);
                              return `${date.getMonth() + 1}/${date.getDate()}`;
                            }}
                          />
                          <YAxis domain={[0, 100]} tick={{ fontSize: 11 }} />
                          <Tooltip
                            formatter={(value) => [`${value}`, 'Score']}
                            labelFormatter={(label) => `Date: ${label}`}
                          />
                          <Line
                            type="monotone"
                            dataKey="score"
                            stroke="#d6336c"
                            strokeWidth={2.5}
                            dot={{ fill: '#d6336c', r: 4 }}
                            activeDot={{ r: 6 }}
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    </Card>
                  </Grid.Col>
                )}
                {dexaTrend.length >= 2 && (
                  <Grid.Col span={{ base: 12, md: labTrend.length >= 2 ? 6 : 12 }}>
                    <Card withBorder radius="md" p="md" className="dashboard-card">
                      <Text size="sm" fw={600} mb="sm">DEXA Scores Over Time</Text>
                      <ResponsiveContainer width="100%" height={220}>
                        <LineChart data={dexaTrend}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#f1f3f5" />
                          <XAxis
                            dataKey="date"
                            tick={{ fontSize: 11 }}
                            tickFormatter={(d) => {
                              const date = new Date(d);
                              return `${date.getMonth() + 1}/${date.getDate()}`;
                            }}
                          />
                          <YAxis domain={[0, 100]} tick={{ fontSize: 11 }} />
                          <Tooltip
                            formatter={(value) => [`${value}`, 'Score']}
                            labelFormatter={(label) => `Date: ${label}`}
                          />
                          <Line
                            type="monotone"
                            dataKey="score"
                            stroke="#8ba3c7"
                            strokeWidth={2.5}
                            dot={{ fill: '#8ba3c7', r: 4 }}
                            activeDot={{ r: 6 }}
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    </Card>
                  </Grid.Col>
                )}
              </Grid>
            </div>
          </>
        )}

        {/* Single report hint */}
        {(labTrend.length === 1 || dexaTrend.length === 1) && labTrend.length < 2 && dexaTrend.length < 2 && (
          <Alert variant="light" color="gray" radius="md">
            <Text size="sm">
              Upload more reports over time to see how your scores trend.
            </Text>
          </Alert>
        )}

        {/* ---- Section 4: Wearables Snapshot ---- */}
        <Divider />
        <div>
          <Group justify="space-between" mb="sm">
            <Title order={3} size="h4" className="dashboard-section-title">Wearables</Title>
            {wearableConnected && (
              <Button
                component={Link}
                to="/wearables/dashboard"
                variant="subtle"
                color="pink"
                size="compact-sm"
                rightSection={<IconChevronRight size={14} />}
              >
                View Details
              </Button>
            )}
          </Group>

          {wearableConnected ? (
            <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="sm">
              <Card withBorder radius="md" p="md" component={Link} to="/wearables/heart-rate" className="dashboard-card dashboard-link-card" style={{ textDecoration: 'none', cursor: 'pointer', transition: 'background 0.15s' }}>
                <Group justify="space-between" mb="xs">
                  <Text size="sm" c="dimmed">Resting Heart Rate</Text>
                  <IconHeart size={20} color="var(--mantine-color-red-6)" />
                </Group>
                <Text size="xl" fw={700}>
                  {wearableData.restingHR != null ? `${Math.round(wearableData.restingHR)} bpm` : '--'}
                </Text>
                <Text size="xs" c="dimmed">
                  {wearableData.restingHRDate
                    ? new Date(wearableData.restingHRDate).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
                    : 'No data'}
                </Text>
              </Card>

              <Card withBorder radius="md" p="md" component={Link} to="/wearables/sleep" className="dashboard-card dashboard-link-card" style={{ textDecoration: 'none', cursor: 'pointer', transition: 'background 0.15s' }}>
                <Group justify="space-between" mb="xs">
                  <Text size="sm" c="dimmed">Sleep</Text>
                  <IconMoon size={20} color="var(--mantine-color-indigo-6)" />
                </Group>
                <Text size="xl" fw={700}>
                  {wearableData.sleep != null ? formatDuration(wearableData.sleep) : '--'}
                </Text>
                <Text size="xs" c="dimmed">
                  {wearableData.sleepDate
                    ? new Date(wearableData.sleepDate).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
                    : 'No data'}
                </Text>
              </Card>

              <Card withBorder radius="md" p="md" component={Link} to="/wearables/recovery" className="dashboard-card dashboard-link-card" style={{ textDecoration: 'none', cursor: 'pointer', transition: 'background 0.15s' }}>
                <Group justify="space-between" mb="xs">
                  <Text size="sm" c="dimmed">Recovery</Text>
                  <IconBolt size={20} color="var(--mantine-color-yellow-6)" />
                </Group>
                {wearableData.recovery != null ? (
                  <>
                    <Text
                      size="xl"
                      fw={700}
                      c={wearableData.recovery >= 67 ? 'green' : wearableData.recovery >= 34 ? 'yellow' : 'red'}
                    >
                      {Math.round(wearableData.recovery)}%
                    </Text>
                    <Text size="xs" c="dimmed">
                      {wearableData.restingHR
                        ? `Resting HR: ${Math.round(wearableData.restingHR)} bpm • `
                        : ''}
                      {wearableData.recoveryDate
                        ? new Date(wearableData.recoveryDate).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
                        : ''}
                    </Text>
                  </>
                ) : (
                  <Text size="sm" c="dimmed">No data</Text>
                )}
              </Card>
            </SimpleGrid>
          ) : (
            <Card withBorder radius="md" p="lg" className="dashboard-card" style={{ textAlign: 'center' }}>
              <Stack gap="sm" align="center">
                <ThemeIcon size={48} radius="xl" variant="light" color="violet">
                  <IconDeviceWatch size={24} />
                </ThemeIcon>
                <Text fw={600}>No wearable connected</Text>
                <Text size="sm" c="dimmed">Connect your Garmin or Whoop to see activity, sleep, and recovery data.</Text>
                <Button
                  component={Link}
                  to="/wearables/devices"
                  variant="light"
                  color="pink"
                  mt="xs"
                >
                  Connect a Device
                </Button>
              </Stack>
            </Card>
          )}
        </div>

        </Stack>
      </Container>
    </div>
  );
}

export default HealthDashboard;
